import { Stack, StackProps } from '@mui/material';
import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { hasValue, renderValue } from './filter.utils';
import { FilterChip, FilterChipProps } from './FilterChip';

export interface ChipLabelItem {
    id: string;
    label: string;
    value?: string;
}

export interface FilterChipsProps<TFilter extends Record<string, any>> {
    /** The current filter values */
    filter: TFilter;
    /** Label mapping for filter keys */
    labels: Record<string, string>;
    /** Default filter values for reset */
    defaultFilters: TFilter;
    /** Called when filter changes */
    onFilterChange: (filter: TFilter) => void;
    /** Keys to hide from chip display */
    hideKeys?: string[];
    /** External label lookup for async-loaded values (e.g., from API) */
    chipLabels?: Record<string, ChipLabelItem[]>;
    /**
     * Custom label renderer for special cases (e.g., Rating, Range values).
     * Return the rendered label or undefined to use default rendering.
     */
    renderLabel?: (params: {
        key: string;
        value: any;
        label: string;
        chipLabels?: Record<string, ChipLabelItem[]>;
    }) => ReactNode | undefined;
    /**
     * Custom chip props generator for specific keys.
     * Useful for adding avatars to assignee chips.
     */
    getChipProps?: (params: {
        key: string;
        value: any;
        index?: number;
    }) => Partial<FilterChipProps> | undefined;
    /** Props for the Stack container */
    stackProps?: StackProps;
}

/**
 * Renders filter chips from a filter object.
 * Handles both array values (multiple chips) and single values.
 * Must be used within a FormProvider context.
 */
export function FilterChips<TFilter extends Record<string, any>>({
    filter,
    labels,
    defaultFilters,
    onFilterChange,
    hideKeys = [],
    chipLabels,
    renderLabel,
    getChipProps,
    stackProps,
}: FilterChipsProps<TFilter>) {
    const { setValue } = useFormContext();

    const handleDeleteArrayItem = (key: string, value: any[], index: number) => {
        const updatedValue = value.filter((_, i) => i !== index);
        const newValue = updatedValue.length ? updatedValue : defaultFilters[key];
        onFilterChange({ ...filter, [key]: newValue });
        setValue(key, newValue);
    };

    const handleDeleteSingleItem = (key: string) => {
        onFilterChange({ ...filter, [key]: defaultFilters[key] });
        setValue(key, defaultFilters[key]);
    };

    const getLabel = (key: string, value: any): ReactNode => {
        const labelText = labels[key] || key;

        // Try custom renderer first
        if (renderLabel) {
            const customLabel = renderLabel({ key, value, label: labelText, chipLabels });
            if (customLabel !== undefined) {
                return customLabel;
            }
        }

        // Default rendering
        return renderValue({
            label: labelText,
            key,
            value,
            chipLabels,
        });
    };

    return (
        <Stack gap={0.5} direction="row" flexWrap="wrap" {...stackProps}>
            {Object.entries(filter || {}).map(([key, value]) => {
                // Skip hidden keys
                if (hideKeys.includes(key)) {
                    return null;
                }

                // Handle array values - render multiple chips
                if (Array.isArray(value)) {
                    return value.map((item, index) => {
                        const customProps = getChipProps?.({ key, value: item, index });
                        return (
                            <FilterChip
                                key={`${key}-${index}`}
                                label={getLabel(key, item)}
                                onDelete={() => handleDeleteArrayItem(key, value, index)}
                                {...customProps}
                            />
                        );
                    });
                }

                // Handle single values
                if (hasValue(value)) {
                    const customProps = getChipProps?.({ key, value });
                    return (
                        <FilterChip
                            key={key}
                            label={getLabel(key, value)}
                            onDelete={() => handleDeleteSingleItem(key)}
                            {...customProps}
                        />
                    );
                }

                return null;
            })}
        </Stack>
    );
}

export default FilterChips;
